// Inspired by https://www.intertech.com/Blog/angular-4-tutorial-handling-refresh-token-with-new-httpinterceptor/

import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpHandler, HttpRequest, HttpResponse, HttpErrorResponse, HttpSentEvent, HttpProgressEvent, HttpHeaderResponse, HttpUserEvent } from '@angular/common/http';

import { throwError as observableThrowError, Observable, BehaviorSubject } from 'rxjs';
// NOTE! Also rxjs dependencies in AuthService must be considerered. Weird errors occur otherwise
import { take, filter, map, catchError, switchMap, finalize } from 'rxjs/operators';

import { AuthService } from './services/auth.service';
import { User } from './models/user.model';
import { ConfigurationService } from './services/configuration.service';




/*
 * When we get a 401 error from the server we try to refresh the access token automatically *
 */
@Injectable()
export class RefreshTokenHttpInterceptor implements HttpInterceptor {

    private readonly _loginUrl: string = "/connect/token";
    private get loginUrl() { return this.configurations.baseUrl + this._loginUrl; }

    private isRefreshingToken: boolean;
    tokenSubject: BehaviorSubject<User> = new BehaviorSubject<User>(null);

    constructor(private authService: AuthService, protected configurations: ConfigurationService) {

    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpSentEvent | HttpHeaderResponse | HttpProgressEvent | HttpResponse<any> | HttpUserEvent<any>> {
        return next.handle(req).pipe(
            catchError(error => {
                if (error instanceof HttpErrorResponse) {
                    switch ((<HttpErrorResponse>error).status) {
                        //case 400:
                        //    return this.handle400Error(error);
                        case 401:
                            return this.handle401Error(req, next);
                        default:
                            return observableThrowError(error);
                    }
                } else {
                    return observableThrowError(error);
                }
            }));
    }

    addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
        return req.clone({ setHeaders: { Authorization: 'Bearer ' + token } });
    }

    handle401Error(req: HttpRequest<any>, next: HttpHandler) {
        if (!this.isRefreshingToken) {
            this.isRefreshingToken = true;

            // Reset here so that the following requests wait until the token
            // comes back from the refreshToken call.
            this.tokenSubject.next(null);

            return this.authService.refreshLogin().pipe(
                switchMap((token: User) => {
                    if (token) {
                        this.tokenSubject.next(token);
                        let newToken = this.authService.accessToken;
                        return next.handle(this.addToken(req, newToken));
                    } else {
                        this.authService.redirectLogoutUser();
                    }

                }),
                catchError(error => {
                    if (error.status === 401 ||
                        (error.url && error.url.toLowerCase().includes(this.loginUrl.toLowerCase()))) {
                        this.authService.reLogin();
                        return observableThrowError('Session expired');
                    } else {
                        return observableThrowError(error || 'Server error');
                    }
                }),
                finalize(() => {
                    this.isRefreshingToken = false;
                }));
        } else {
            return this.tokenSubject.pipe(
                filter(token => token != null),
                take(1),
                switchMap(token => {
                    let newToken = this.authService.accessToken;
                    return next.handle(this.addToken(req, newToken));
                }));
        }
    }

    // Denna kod är aldrig testad
    handle400Error(error) {
        if (error && error.status === 400 && error.error && error.error.error === 'invalid_grant') {
            // If we get a 400 and the error message is 'invalid_grant', the token is no longer valid so logout.
            return this.authService.redirectLogoutUser();
        }

        return observableThrowError(error);
    }
}
